import { clsx } from 'clsx'
import Image from 'next/image'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { useActivateContentsquareExperiment } from 'contentsquare'

import { Experiment } from '../../../../experiments/components/Experiment'
import { ExperimentVariant } from '../../../../experiments/components/ExperimentVariant'
import {
  EXPERIMENTS_TRACK_EVENT_HEADER_MOBILE_LOGO,
  experimentsTrackEvent,
} from '../../../../experiments/eventTracking'
import { HeaderMobileIconListExperiment } from '../../../../experiments/header-mobile-icon-list-experiment/HeaderMobileIconListExperiment'
import { HeaderMobileIconListExperimentLogo } from '../../../../experiments/header-mobile-icon-list-experiment/HeaderMobileIconListExperimentLogo'
import { useDetectScrollUpForHeaderStickyToggleExperiment } from '../../../../experiments/header-sticky-toggle-experiment/hooks/useDetectScrollUpForHeaderStickyToggleExperiment'
import {
  useGetExperiment,
  useGetExperimentForTenant,
} from '../../../../experiments/hooks'
import { EXPERIMENT_NAME_HEADER_MOBILE_ICON_LIST } from '../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import { experimentAccessorIsEnabledAndVariant } from '../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../global-config'
import { urlResolverGetHomepage } from '../../../../url-handling'
import { HeaderSearchActiveSearchBox } from '../../header-search/components/HeaderSearchActiveSearchBox/HeaderSearchActiveSearchBox'
import { useHeaderSearchContext } from '../../header-search/headerSearchContext'
import { useHeaderDataContext } from '../../headerConfigContext'
import { headerCmsBlockMenuAccessorGetHamburgerMenu } from '../../model/headerCmsBlockMenuAccessor'
import { OffCanvasHeaderMenuContainer } from '../../off-canvas-header-menu/components/OffCanvasHeaderMenuContainer'

import { HeaderMobileIconList } from './HeaderMobileIconList'

import styles from './HeaderMobile.module.css'

export const HeaderMobile: FC = () => {
  const headerData = useHeaderDataContext()
  const headerSearchContext = useHeaderSearchContext()
  const publicConfig = useGlobalConfigContext()

  const logo = headerData.shopLogos.mobile
  useActivateContentsquareExperiment(EXPERIMENT_NAME_HEADER_MOBILE_ICON_LIST)

  const { variant } = useGetExperiment(EXPERIMENT_NAME_HEADER_MOBILE_ICON_LIST)
  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )
  const isHeaderStickyToggleExperimentV1 =
    experimentAccessorIsEnabledAndVariant(
      headerStickyToggleExperiment,
      EXPERIMENT_VARIATION.V1,
    )
  const isHeaderStickyToggleExperimentV3 =
    experimentAccessorIsEnabledAndVariant(
      headerStickyToggleExperiment,
      EXPERIMENT_VARIATION.V3,
    )

  const intl = useIntl()

  const isScrollingUp = useDetectScrollUpForHeaderStickyToggleExperiment(
    headerStickyToggleExperiment,
  )
  const isHeaderStickyToggleExperimentHeaderPositionStatic =
    (isHeaderStickyToggleExperimentV1 || isHeaderStickyToggleExperimentV3) &&
    !isScrollingUp

  return (
    <>
      <header
        className={clsx(
          styles['header-mobile'],
          isHeaderStickyToggleExperimentHeaderPositionStatic &&
            styles['header-mobile-static'],
        )}
      >
        <OffCanvasHeaderMenuContainer
          hamburgerMenuData={headerCmsBlockMenuAccessorGetHamburgerMenu(
            headerData.cmsMenu,
          )}
        />
        <div>
          <a
            href={urlResolverGetHomepage(publicConfig)}
            onClick={() => {
              experimentsTrackEvent(
                publicConfig,
                EXPERIMENTS_TRACK_EVENT_HEADER_MOBILE_LOGO,
              )
            }}
          >
            {variant === EXPERIMENT_VARIATION.V1 ? (
              <HeaderMobileIconListExperimentLogo />
            ) : (
              <Image alt={intl.formatMessage({ id: 'shop.name' })} src={logo} />
            )}
          </a>
        </div>
        <Experiment names={[EXPERIMENT_NAME_HEADER_MOBILE_ICON_LIST]}>
          <ExperimentVariant name={EXPERIMENT_VARIATION.DEFAULT}>
            <HeaderMobileIconList />
          </ExperimentVariant>
          <ExperimentVariant name={EXPERIMENT_VARIATION.V1}>
            <HeaderMobileIconListExperiment />
          </ExperimentVariant>
        </Experiment>
      </header>
      {headerSearchContext.searchIsActive ? (
        <HeaderSearchActiveSearchBox
          /*
           * On `desktop`, search box is always a portal to `HeaderSearchDesktop`,
           * on mobile it is rendered without portal here.
           */
          className={clsx(
            !headerSearchContext.portalResultsTo && 'tablet:hidden',
          )}
        />
      ) : null}
    </>
  )
}
